import { useRef, useCallback, useMemo, useState } from "react";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import { Textarea } from "@/frontend/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/frontend/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/frontend/components/ui/alert-dialog";
import { ImageUploadField } from "./ImageUploadField";
import { MultiImageUploadField } from "./MultiImageUploadField";
import { TypologyCombobox } from "./TypologyCombobox";
import { RoomType } from "@/shared/types";

export interface RoomTypeFieldsData {
  name: string;
  description: string;
  maxOccupancy: number;
  priceByOccupancy: number[];
  minStay: number;
  pricePerExcessKwh?: number;
  area: number;
  floorPlanUrl: string;
  imagesUrl: string[];
  typologyKey?: string;
}

interface Props {
  data: RoomTypeFieldsData;
  onChange: (data: RoomTypeFieldsData) => void;
  roomId?: string;
  onUploadingChange?: (uploading: boolean) => void;
  /** All rooms of the current residence — used to suggest existing typologies. */
  residenceRooms?: RoomType[];
  /** Current number of units in the typology. */
  unitCount?: number;
  /** When provided (and a typology is set), shows the "Number of units" field. */
  onUnitCountChange?: (count: number) => void;
  /** Minimum allowed unit count (e.g. when editing, the count cannot drop below 1). */
  minUnitCount?: number;
  /** When true (editing an existing room), joining an existing typology asks for
   *  confirmation before overwriting the form with that typology's values. */
  isEditing?: boolean;
}

export function RoomTypeFormFields({
  data,
  onChange,
  roomId,
  onUploadingChange,
  residenceRooms = [],
  unitCount = 1,
  onUnitCountChange,
  minUnitCount = 1,
  isEditing = false,
}: Props) {
  const set = (patch: Partial<RoomTypeFieldsData>) => onChange({ ...data, ...patch });
  const [pendingTypology, setPendingTypology] = useState<string | null>(null);

  const typologyOptions = useMemo(() => {
    const keys = residenceRooms
      .map((r) => r.typologyKey)
      .filter((k): k is string => !!k && k.trim().length > 0);
    return Array.from(new Set(keys)).sort();
  }, [residenceRooms]);

  // Copy an existing typology's shared fields into the form. Saving then
  // propagates these (identical) values back out, keeping the group consistent.
  const applyTypologyFromSibling = (value: string) => {
    const sibling = residenceRooms.find(
      (r) => r.typologyKey === value && r.id !== roomId
    );
    if (sibling) {
      set({
        typologyKey: value,
        name: sibling.name,
        description: sibling.description,
        imagesUrl: sibling.imagesUrl,
        floorPlanUrl: sibling.floorPlanUrl,
        area: sibling.area,
        priceByOccupancy: sibling.priceByOccupancy,
        maxOccupancy: sibling.maxOccupancy,
        minStay: sibling.minStay,
        pricePerExcessKwh: sibling.pricePerExcessKwh,
      });
    } else {
      set({ typologyKey: value });
    }
  };

  // How many units are currently in this room's typology (used in the confirm copy).
  const currentTypologySize = data.typologyKey
    ? residenceRooms.filter((r) => r.typologyKey === data.typologyKey).length
    : 1;

  const handleTypologyChange = (value: string | undefined) => {
    // Clearing the typology, or re-selecting the current one — nothing is overwritten.
    if (!value || value === data.typologyKey) {
      set({ typologyKey: value });
      return;
    }
    // A brand-new typology has no shared values to inherit — no data is lost.
    const hasSiblingData = residenceRooms.some(
      (r) => r.typologyKey === value && r.id !== roomId
    );
    if (!hasSiblingData) {
      set({ typologyKey: value });
      return;
    }
    // Joining an existing typology overwrites the shared fields. When editing an
    // existing room, confirm first so a mis-click can't silently wipe its details.
    if (isEditing) {
      setPendingTypology(value);
    } else {
      applyTypologyFromSibling(value);
    }
  };

  // Track uploading state across floor plan + images
  const uploadingFlags = useRef({ floorPlan: false, images: false });
  const notifyUploading = useCallback(() => {
    onUploadingChange?.(uploadingFlags.current.floorPlan || uploadingFlags.current.images);
  }, [onUploadingChange]);

  const storagePath = roomId || '';

  return (
    <div className="grid gap-4">
      {/* Name */}
      <div className="space-y-2">
        <Label>Name *</Label>
        <Input
          value={data.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="e.g., Studio Apartment"
        />
      </div>

      {/* Typology */}
      <div className="space-y-2">
        <Label>Typology</Label>
        <TypologyCombobox
          value={data.typologyKey}
          onChange={handleTypologyChange}
          options={typologyOptions}
        />
        {data.typologyKey ? (
          <p className="text-xs text-muted-foreground">
            All units of typology "{data.typologyKey}" share the details below. Saving applies any change to every unit.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Optional. Group identical units (e.g. "T3-A") so they show as one public listing. Leave empty for whole-apartment lets.
          </p>
        )}
      </div>

      {/* Number of units — only when this room belongs to a typology */}
      {onUnitCountChange && data.typologyKey && (
        <div className="space-y-2">
          <Label>Number of units</Label>
          <Input
            type="number"
            min={minUnitCount}
            max={50}
            value={unitCount || ""}
            onChange={(e) => onUnitCountChange(Number(e.target.value))}
            onBlur={(e) =>
              onUnitCountChange(
                Math.max(minUnitCount, Math.min(50, Number(e.target.value) || minUnitCount))
              )
            }
          />
          <p className="text-xs text-muted-foreground">
            How many identical units this typology has. They all share the details below.
          </p>
        </div>
      )}

      {/* Description */}
      <div className="space-y-2">
        <Label>Description *</Label>
        <Textarea
          value={data.description}
          onChange={(e) => set({ description: e.target.value })}
          rows={3}
          placeholder="Describe the room..."
        />
      </div>

      {/* Max Occupancy + Min Stay */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Max Occupancy *</Label>
          <Select
            value={String(data.maxOccupancy)}
            onValueChange={(value) => {
              const newMax = Number(value);
              const current = data.priceByOccupancy;
              let updated: number[];
              if (newMax > current.length) {
                const lastPrice = current[current.length - 1] || 0;
                updated = [...current, ...Array(newMax - current.length).fill(lastPrice)];
              } else {
                updated = current.slice(0, newMax);
              }
              set({ maxOccupancy: newMax, priceByOccupancy: updated });
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4].map((num) => (
                <SelectItem key={num} value={String(num)}>
                  {num} {num === 1 ? "person" : "people"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Minimum Stay (months) *</Label>
          <Input
            type="number"
            min="1"
            value={data.minStay || ""}
            onChange={(e) => set({ minStay: Number(e.target.value) })}
          />
        </div>
      </div>

      {/* Price by Occupancy */}
      <div className="space-y-2">
        <Label>Price by Occupancy (€/month) *</Label>
        <div className="space-y-2">
          {Array.from({ length: data.maxOccupancy }, (_, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground w-28 shrink-0">
                {i + 1} {i === 0 ? "student" : "students"}
              </span>
              <Input
                type="number"
                min="1"
                value={(data.priceByOccupancy)[i] || ""}
                onChange={(e) => {
                  const updated = [...data.priceByOccupancy];
                  updated[i] = Number(e.target.value);
                  set({ priceByOccupancy: updated });
                }}
                placeholder="e.g. 350"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Price per excess kWh */}
      <div className="space-y-2">
        <Label>Price per excess kWh (€)</Label>
        <Input
          type="number"
          min="0.01"
          step="0.01"
          value={data.pricePerExcessKwh ?? ""}
          onChange={(e) =>
            set({ pricePerExcessKwh: e.target.value ? Number(e.target.value) : undefined })
          }
          placeholder="e.g. 0.25"
        />
        <p className="text-xs text-muted-foreground">
          Amount charged per kWh consumed above the monthly limit. Leave empty if not applicable.
        </p>
      </div>

      {/* Area */}
      <div className="space-y-2">
        <Label>Area (m²) *</Label>
        <Input
          type="number"
          min="1"
          step="0.01"
          value={data.area || ""}
          onChange={(e) => set({ area: Number(e.target.value) })}
          placeholder="e.g., 25.5"
        />
      </div>

      {/* Floor Plan */}
      <ImageUploadField
        value={data.floorPlanUrl}
        onChange={(url) => set({ floorPlanUrl: url })}
        storagePath={`rooms/${storagePath}/floorplan`}
        label="Floor Plan (optional)"
        onUploadingChange={(uploading) => {
          uploadingFlags.current.floorPlan = uploading;
          notifyUploading();
        }}
      />

      {/* Room Images */}
      <MultiImageUploadField
        values={data.imagesUrl}
        onChange={(urls) => set({ imagesUrl: urls })}
        storagePathPrefix={`rooms/${storagePath}`}
        label="Room Images"
        required
        onUploadingChange={(uploading) => {
          uploadingFlags.current.images = uploading;
          notifyUploading();
        }}
      />

      {/* Confirm before joining an existing typology (edit mode only) */}
      <AlertDialog
        open={pendingTypology !== null}
        onOpenChange={(open) => {
          if (!open) setPendingTypology(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Join typology "{pendingTypology}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {currentTypologySize > 1
                ? `All ${currentTypologySize} units of typology "${data.typologyKey}" will join "${pendingTypology}" and take on its shared values: name, description, pricing, area, photos and floor plan. This cannot be undone.`
                : `This room will join typology "${pendingTypology}" and take on its shared values: name, description, pricing, area, photos and floor plan. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const target = pendingTypology;
                setPendingTypology(null);
                if (target) applyTypologyFromSibling(target);
              }}
            >
              Join typology
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
